import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { uploadToCloudinary } from "@/lib/cloudinary";
import { Upload, Loader2, Trash2, Video as VideoIcon, Image as ImageIcon, Plus } from "lucide-react";

const CATEGORIES = ["Garden", "Joinery", "Painting", "Property", "Other"];

export default function GalleryManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ title: "", caption: "", category: "Garden" });
  const [pendingFile, setPendingFile] = useState(null);

  const load = async () => {
    try { const r = await api.get("/gallery?limit=100"); setItems(r.data || []); }
    catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Client-side size cap: 100MB video / 15MB image
    const isVideo = f.type.startsWith("video/");
    const cap = isVideo ? 100 * 1024 * 1024 : 15 * 1024 * 1024;
    if (f.size > cap) {
      setError(`File too large (${(f.size / (1024*1024)).toFixed(1)}MB). Max ${isVideo ? "100MB video" : "15MB image"}.`);
      return;
    }
    setError("");
    setPendingFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!pendingFile) { setError("Choose a file first."); return; }
    if (!form.title.trim()) { setError("Add a title."); return; }
    setUploading(true); setError("");
    try {
      const up = await uploadToCloudinary(pendingFile, "gallery");
      const created = await api.post("/gallery", {
        type: up.resource_type === "video" ? "video" : "photo",
        url: up.secure_url,
        thumbnail_url: up.thumbnail_url,
        title: form.title.trim(),
        caption: form.caption.trim(),
        category: form.category,
      });
      setItems([created.data, ...items]);
      setForm({ title: "", caption: "", category: "Garden" });
      setPendingFile(null);
      const fileInput = document.getElementById("gallery-file-input");
      if (fileInput) fileInput.value = "";
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || err?.message || "Upload failed.");
    } finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this item from the gallery?")) return;
    try { await api.delete(`/gallery/${id}`); setItems(items.filter((x) => x.id !== id)); }
    catch (e) { alert("Delete failed"); }
  };

  return (
    <div data-testid="gallery-manager" className="space-y-6">
      <form onSubmit={submit} data-testid="gallery-upload-form" className="border border-[#1F3325] bg-[#0F1712] p-6">
        <div className="font-display uppercase tracking-widest text-xs text-[#8DE038] mb-4">/ Add a new transformation</div>

        <label className="block cursor-pointer" data-testid="gallery-upload-label">
          <input id="gallery-file-input" type="file" accept="image/*,video/*" onChange={onFile} className="hidden" data-testid="gallery-file-input"/>
          <div className={`border-2 border-dashed p-8 text-center transition-colors ${pendingFile ? "border-[#8DE038] bg-[#8DE038]/5" : "border-[#1F3325] hover:border-[#8DE038]"}`}>
            {pendingFile ? (
              <div className="flex items-center justify-center gap-3 text-[#8DE038]">
                {pendingFile.type.startsWith("video/") ? <VideoIcon size={22}/> : <ImageIcon size={22}/>}
                <span className="text-sm font-display uppercase tracking-widest">{pendingFile.name}</span>
                <span className="text-xs text-[#9CAEA3]">({(pendingFile.size/(1024*1024)).toFixed(1)}MB)</span>
              </div>
            ) : (
              <div className="text-[#9CAEA3]">
                <Upload className="inline mr-2" size={16}/> Click to choose a photo or video
                <div className="text-[10px] uppercase tracking-widest text-[#6B7C71] mt-2">Photos ≤ 15MB · Videos ≤ 100MB · Auto-optimised on delivery</div>
              </div>
            )}
          </div>
        </label>

        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <input data-testid="gallery-title" required placeholder="Title (e.g. 'Front garden refresh — Chapel Allerton')" value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} className="gl-input"/>
          <select data-testid="gallery-category" value={form.category} onChange={(e)=>setForm({...form, category: e.target.value})} className="gl-input">
            {CATEGORIES.map((c)=>(<option key={c} value={c}>{c}</option>))}
          </select>
        </div>
        <textarea data-testid="gallery-caption" rows={2} placeholder="Short caption (optional)" value={form.caption} onChange={(e)=>setForm({...form, caption: e.target.value})} className="gl-input resize-none mt-3"/>

        {error && <div className="mt-3 text-red-400 text-xs border border-red-500/40 bg-red-900/20 p-2">{error}</div>}

        <button data-testid="gallery-submit" type="submit" disabled={uploading || !pendingFile} className="btn-primary mt-4 w-full justify-center disabled:opacity-60">
          {uploading ? <><Loader2 className="animate-spin" size={16}/> Uploading…</> : <><Plus size={16}/> Publish to gallery</>}
        </button>
      </form>

      <div>
        <div className="font-display uppercase tracking-widest text-xs text-[#9CAEA3] mb-3">/ Current gallery ({items.length})</div>
        {loading ? (
          <div className="text-[#9CAEA3]">Loading…</div>
        ) : items.length === 0 ? (
          <div data-testid="gallery-manager-empty" className="text-[#9CAEA3] border border-dashed border-[#1F3325] p-8 text-center">No items yet. Upload your first transformation above.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((g) => (
              <div key={g.id} data-testid={`gallery-manager-item-${g.id}`} className="relative border border-[#1F3325] bg-[#0F1712] group">
                <div className="aspect-square overflow-hidden bg-[#050806]">
                  {g.type === "video" ? (
                    <div className="w-full h-full flex items-center justify-center relative">
                      <img src={g.thumbnail_url || g.url.replace(/\.[^/.]+$/, ".jpg")} alt={g.title} className="absolute inset-0 w-full h-full object-cover"/>
                      <VideoIcon size={40} className="relative text-white drop-shadow-2xl"/>
                    </div>
                  ) : (
                    <img src={g.url} alt={g.title} className="w-full h-full object-cover"/>
                  )}
                </div>
                <div className="p-3">
                  <div className="font-display uppercase tracking-widest text-[11px] text-[#F0F4F1] truncate">{g.title}</div>
                  {g.category && <div className="text-[9px] text-[#8DE038] uppercase tracking-widest mt-1">{g.category}</div>}
                </div>
                <button data-testid={`gallery-delete-${g.id}`} onClick={()=>remove(g.id)} className="absolute top-2 right-2 w-8 h-8 bg-black/70 text-red-400 hover:bg-red-500 hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Delete"><Trash2 size={14}/></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
